import {
  Avatar,
  Button,
  CircularProgress,
  Grid,
  makeStyles,
  Tooltip,
} from '@material-ui/core';
import {
  InfoOutlined as InfoOutlinedIcon,
  Visibility as VisibilityIcon,
} from '@material-ui/icons';
import clsx from 'clsx';
import AutoCard from 'components/AutoCard';
import useWindowDimensions from 'hooks/useWindowWidth';
import i18n from 'i18n';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import { CardConfig, CardOption } from '../interfaces';
import DashboardCardModal from './DashboardCardModal';
import DoughnutGraph from './Graphs/Doughnut';
import Legend from './Legend';
import styles, { overrideCardDoughnut, overrideModalStatus } from './styles';

const useStyles = makeStyles(styles);

interface DashboardCardProps {
  cardConfig: CardConfig;
  companySelected: CompanyData | null;
  filters?: Record<string, string | string[]>;
  filterTooltip: string;
  infoText?: string;
  isConfig?: boolean;
  isLoading?: boolean;
  modalTitle: string;
  role?: RolesEnum;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  updateFilter?: (filter: Record<string, string | string[]>) => void;
}

const DashboardCard = ({
  cardConfig,
  companySelected,
  filters,
  filterTooltip,
  infoText,
  isConfig,
  isLoading,
  modalTitle,
  role,
  modalClose,
  modalOpen,
  updateFilter,
}: DashboardCardProps) => {
  const classes = useStyles();
  const { width } = useWindowDimensions();

  const handleGetGraphs = () => {
    modalOpen({
      overrideClasses: overrideModalStatus,
      title: i18n.t(modalTitle),
      footer: (
        <Button id="cancel-button" variant="outlined" onClick={modalClose}>
          {i18n.t('CLOSE_KEY')}
        </Button>
      ),
      content: (
        <DashboardCardModal
          values={cardConfig.options?.map(option => ({
            ...option,
            count: cardConfig.report[option.field] as number,
          }))}
        />
      ),
      maxWidth: 'xl',
    });
  };

  const isSelected = (option: CardOption) => {
    if (!filters || !filters[cardConfig.key]) return false;

    const value = filters[cardConfig.key];
    if (Array.isArray(value)) {
      return value.includes(option.value);
    }
    return value === option.value;
  };

  const handleClickOnFilter = (option: CardOption, value: string) => {
    if (!updateFilter) return;

    let filterValue: string | string[] = cardConfig.multiple ? [] : '';

    const isMultipleSelected =
      cardConfig.multiple && filters && Array.isArray(filters[cardConfig.key]);

    if (value) {
      if (isMultipleSelected) {
        filterValue = [...(filters?.[cardConfig.key] as string[]), value];
      } else {
        filterValue = cardConfig.multiple ? [value] : value;
      }
    } else if (isMultipleSelected) {
      filterValue = (filters?.[cardConfig.key] as string[]).filter(
        item => item !== option.value
      );
    }

    updateFilter({
      [cardConfig.key]: filterValue,
    });
  };

  const [showAll, setShowAll] = React.useState(false);

  const hasManyItems = cardConfig?.options?.length > 9;
  const visibleOptions = showAll
    ? cardConfig?.options
    : cardConfig?.options?.slice(0, 8);

  return (
    <AutoCard overrideClasses={overrideCardDoughnut}>
      {isLoading ? (
        <Grid container justify="center" className={classes.gridAutoCardLoad}>
          <CircularProgress />
        </Grid>
      ) : (
        <>
          <Grid
            container
            justify="space-between"
            className={clsx(classes.gridAutoCard, {
              [classes.gridAutoCardModal]: isConfig,
              [classes.gridAutoCardModalManyItems]: hasManyItems && showAll,
            })}
          >
            <Grid item xs={8} md={5}>
              <Grid
                className={clsx({
                  [classes.legendManyItems]: hasManyItems && showAll,
                })}
              >
                {visibleOptions?.map(option => {
                  const count = cardConfig?.report[option.field];

                  return (
                    <Legend
                      key={`${cardConfig?.key}-${option.field}`}
                      color={option.color}
                      label={i18n.t(option.label)}
                      count={count}
                      value={option.value}
                      handleClick={value => handleClickOnFilter(option, value)}
                      tooltip={filterTooltip}
                      isConfig={isConfig}
                      isSelected={isSelected(option)}
                    />
                  );
                })}
                {showAll && hasManyItems && (
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={() => setShowAll(false)}
                    className={classes.seeMoreButton}
                  >
                    {i18n.t('SEE_LESS_KEY')}
                  </Button>
                )}
              </Grid>
              {!showAll && hasManyItems && (
                <Button
                  variant="text"
                  color="secondary"
                  onClick={() => setShowAll(true)}
                  style={{ padding: '0px 4px', marginLeft: 10 }}
                >
                  {i18n.t('SEE_MORE_KEY')}
                </Button>
              )}

              {width <= 1025 && !isConfig && (
                <Grid xs={12} item className={classes.graphLegendsContainer}>
                  <Button
                    startIcon={<VisibilityIcon />}
                    variant="outlined"
                    onClick={() => handleGetGraphs()}
                    className={classes.detailsButton}
                  >
                    {i18n.t('SEE_DETAILS_KEY')}
                  </Button>
                </Grid>
              )}
            </Grid>
            {width > 1025 && (
              <Grid item style={{ display: 'flex', height: '100%' }}>
                <Grid item className={classes.displayFlex}>
                  <DoughnutGraph
                    values={cardConfig.options?.map(option => ({
                      ...option,
                      count: cardConfig.report[option.field],
                    }))}
                  />
                </Grid>
                <Grid item className={classes.iconGrid}>
                  {!isConfig && (
                    <Avatar
                      onClick={handleGetGraphs}
                      variant="rounded"
                      className={clsx(classes.avatar, {
                        [classes.avatarSuperAdmin]: inSuperAdminView(
                          role,
                          companySelected
                        ),
                        [classes.avatarAdmin]: inAdminView(
                          role,
                          companySelected
                        ),
                        [classes.avatarOperator]: inOperatorView(
                          role,
                          companySelected
                        ),
                        [classes.avatarCompanyContext]: companySelected,
                      })}
                    >
                      <VisibilityIcon className={classes.icon} />
                    </Avatar>
                  )}

                  {infoText && (
                    <Grid>
                      <Tooltip title={i18n.t(infoText)}>
                        <InfoOutlinedIcon className={classes.infoIcon} />
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </AutoCard>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
}: ApplicationState) => ({
  companySelected,
  role: data?.role,
});

export default connect(mapStateToProps, {
  modalOpen,
  modalClose,
})(DashboardCard);
